import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp } from 'react-feather';
import { useTranslations } from 'use-intl';
import { FincluCustomerQuestionnaire } from '@/pages/CustomerSupport/types';
import { formatCurrency } from '@/utils/utils';
interface QuestionnaireSectionProps {
	questionnaire: FincluCustomerQuestionnaire | null;
	isExpanded: boolean;
}

const QuestionnaireSection: React.FC<QuestionnaireSectionProps> = ({
	questionnaire,
	isExpanded: parentIsExpanded
}) => {
	const t = useTranslations('SupportClients');
	const [isExpanded, setIsExpanded] = useState(parentIsExpanded);

	useEffect(() => {
		setIsExpanded(parentIsExpanded);
	}, [parentIsExpanded]);

	const getHouseTypeLabel = (type: string) => {
		const labels: Record<string, string> = {
			rent: t('HouseTypeRent'),
			owned: t('HouseTypeOwned'),
			family_owned: t('HouseTypeFamilyOwned')
		};
		return labels[type] || type;
	};

	const getPlatformPlanLabel = (plan: string) => {
		const labels: Record<string, string> = {
			A: t('PlatformPlanA'),
			B: t('PlatformPlanB')
		};
		return labels[plan] || plan;
	};

	const getModalityLabel = (modality: string) => {
		const labels: Record<string, string> = {
			full_time: t('FullTime'),
			part_time: t('PartTime'),
			not_applicable: t('NotApplicable')
		};
		return labels[modality] || modality;
	};

	if (!questionnaire) {
		return (
			<div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
				<div
					className="flex items-center justify-between cursor-pointer mb-4"
					onClick={() => setIsExpanded(!isExpanded)}
				>
					<h2 className="text-lg font-semibold">{t('QuestionnaireInformation')}</h2>
					{isExpanded ? (
						<ChevronUp size={20} className="text-gray-500" />
					) : (
						<ChevronDown size={20} className="text-gray-500" />
					)}
				</div>
				{isExpanded && <p className="text-gray-500 text-center py-6">{t('NoQuestionnaireData')}</p>}
			</div>
		);
	}

	return (
		<div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
			<div
				className="flex items-center justify-between cursor-pointer mb-4"
				onClick={() => setIsExpanded(!isExpanded)}
			>
				<h2 className="text-lg font-semibold">{t('QuestionnaireInformation')}</h2>
				{isExpanded ? (
					<ChevronUp size={20} className="text-gray-500" />
				) : (
					<ChevronDown size={20} className="text-gray-500" />
				)}
			</div>

			{isExpanded && (
				<>
					<div className="mb-5">
						<h3 className="text-md font-semibold mb-3">{t('PersonalInformation')}</h3>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							{questionnaire.occupation && (
								<div className="flex justify-between">
									<label className="text-sm text-gray-500">{t('Occupation')}:</label>
									<p className="text-sm font-medium">{questionnaire.occupation}</p>
								</div>
							)}
						</div>
					</div>

					<div className="mb-5">
						<h3 className="text-md font-semibold mb-3">{t('FinancialInformation')}</h3>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							{questionnaire.primary_income !== null && (
								<div className="flex justify-between">
									<label className="text-sm text-gray-500">{t('PrimaryIncome')}:</label>
									<p className="text-sm font-medium">
										{formatCurrency(questionnaire.primary_income)}
									</p>
								</div>
							)}
							{questionnaire.secondary_income !== null && (
								<div className="flex justify-between">
									<label className="text-sm text-gray-500">{t('SecondaryIncome')}:</label>
									<p className="text-sm font-medium">
										{formatCurrency(questionnaire.secondary_income)}
									</p>
								</div>
							)}
							{questionnaire.partner_income !== null && (
								<div className="flex justify-between">
									<label className="text-sm text-gray-500">{t('PartnerIncome')}:</label>
									<p className="text-sm font-medium">
										{formatCurrency(questionnaire.partner_income)}
									</p>
								</div>
							)}
							<div className="flex justify-between">
								<label className="text-sm text-gray-500">{t('WantsPlatformIncome')}:</label>
								<p className="text-sm font-medium">
									{questionnaire.wants_platform_income !== null &&
									questionnaire.wants_platform_income !== undefined
										? String(questionnaire.wants_platform_income) === 'true'
											? t('Yes')
											: t('No')
										: t('NotApplicable')}
								</p>
							</div>
							<div className="flex justify-between">
								<label className="text-sm text-gray-500">{t('PlatformIncomeModality')}:</label>
								<p className="text-sm font-medium">
									{questionnaire.platform_income_modality
										? getModalityLabel(questionnaire.platform_income_modality)
										: t('NotApplicable')}
								</p>
							</div>
							<div className="flex justify-between">
								<label className="text-sm text-gray-500">{t('ProjectedPlatformIncome')}:</label>
								<p className="text-sm font-medium">
									{questionnaire.projected_platform_income !== null &&
									questionnaire.projected_platform_income !== undefined &&
									String(questionnaire.projected_platform_income) !== ''
										? formatCurrency(Number(questionnaire.projected_platform_income))
										: t('NotApplicable')}
								</p>
							</div>
						</div>
					</div>

					{(questionnaire.has_mobility_platform !== null ||
						questionnaire.platform_plan ||
						questionnaire.movility_platform_modality) && (
						<div className="mb-5">
							<h3 className="text-md font-semibold mb-3">{t('HasMobilityPlatform')}</h3>
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								{questionnaire.has_mobility_platform !== null && (
									<div className="flex justify-between">
										<label className="text-sm text-gray-500">{t('HasMobilityPlatform')}:</label>
										<p className="text-sm font-medium">
											{questionnaire.has_mobility_platform ? t('Yes') : t('No')}
										</p>
									</div>
								)}
								{questionnaire.platform_plan && (
									<div className="flex justify-between">
										<label className="text-sm text-gray-500">{t('PlatformPlan')}:</label>
										<p className="text-sm font-medium">
											{getPlatformPlanLabel(questionnaire.platform_plan)}
										</p>
									</div>
								)}
								{questionnaire.platform_plan_description && (
									<div className="md:col-span-2 flex justify-between">
										<label className="text-sm text-gray-500">{t('PlatformPlanDescription')}:</label>
										<p className="text-sm font-medium">{questionnaire.platform_plan_description}</p>
									</div>
								)}
								{questionnaire.movility_platform_modality && (
									<div className="flex justify-between">
										<label className="text-sm text-gray-500">{t('WorkModality')}:</label>
										<p className="text-sm font-medium">
											{getModalityLabel(questionnaire.movility_platform_modality)}
										</p>
									</div>
								)}
								{questionnaire.movility_platform_modality_description && (
									<div className="md:col-span-2 flex justify-between">
										<label className="text-sm text-gray-500">{t('WorkModalityDescription')}:</label>
										<p className="text-sm font-medium">
											{questionnaire.movility_platform_modality_description}
										</p>
									</div>
								)}
							</div>
						</div>
					)}

					{(questionnaire.house_type ||
						questionnaire.rent !== null ||
						questionnaire.mortgage_payment !== null) && (
						<div className="mb-5">
							<h3 className="text-md font-semibold mb-3">{t('HouseType')}</h3>
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								{questionnaire.house_type && (
									<div className="flex justify-between">
										<label className="text-sm text-gray-500">{t('HouseType')}:</label>
										<p className="text-sm font-medium">
											{getHouseTypeLabel(questionnaire.house_type)}
										</p>
									</div>
								)}
								{questionnaire.rent !== null && (
									<div className="flex justify-between">
										<label className="text-sm text-gray-500">{t('Rent')}:</label>
										<p className="text-sm font-medium">{formatCurrency(questionnaire.rent)}</p>
									</div>
								)}
								{questionnaire.mortgage_payment !== null && (
									<div className="flex justify-between">
										<label className="text-sm text-gray-500">{t('MortgagePayment')}:</label>
										<p className="text-sm font-medium">
											{formatCurrency(questionnaire.mortgage_payment)}
										</p>
									</div>
								)}
							</div>
						</div>
					)}

					{(questionnaire.family_expenses !== null ||
						questionnaire.fuel_consumption !== null ||
						questionnaire.electric_charge !== null) && (
						<div className="mb-5">
							<h3 className="text-md font-semibold mb-3">{t('MonthlyPayment')}</h3>
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								{questionnaire.family_expenses !== null && (
									<div className="flex justify-between">
										<label className="text-sm text-gray-500">{t('FamilyExpenses')}:</label>
										<p className="text-sm font-medium">
											{formatCurrency(questionnaire.family_expenses)}
										</p>
									</div>
								)}
							</div>
						</div>
					)}

					{(questionnaire.has_partner !== null ||
						questionnaire.economic_dependants !== null ||
						questionnaire.economic_dependants_people_spanish) && (
						<div className="mb-5">
							<h3 className="text-md font-semibold mb-3">{t('FamilyInformation')}</h3>
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								{questionnaire.has_partner !== null && (
									<div className="flex justify-between">
										<label className="text-sm text-gray-500">{t('HasPartner')}:</label>
										<p className="text-sm font-medium">
											{questionnaire.has_partner ? t('Yes') : t('No')}
										</p>
									</div>
								)}
								{questionnaire.economic_dependants !== null && (
									<div className="flex justify-between">
										<label className="text-sm text-gray-500">{t('EconomicDependants')}:</label>
										<p className="text-sm font-medium">{questionnaire.economic_dependants}</p>
									</div>
								)}
								{questionnaire.economic_dependants_people_spanish && (
									<div className="md:col-span-2 flex justify-between">
										<label className="text-sm text-gray-500">
											{t('EconomicDependantsPeople')}:
										</label>
										<p className="text-sm font-medium">
											{questionnaire.economic_dependants_people_spanish}
										</p>
									</div>
								)}
							</div>
						</div>
					)}
				</>
			)}
		</div>
	);
};

export default QuestionnaireSection;
